import * as React from "react";
import * as _ from "lodash";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementBorderType,
    ComponentElementDisplayMode,
    ComponentElementOverflowType,
    ComponentElementStatic,
    JSMap
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentElementPosition } from "@bizform/type/model/data_set/LookUp.model";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { TabSet } from "./BfTabView/TabSet";
import { Tab } from "./BfTabView/Tab";
import { TabModel } from "./BfTabView/Tab.model";
import { I18n } from "../../misc/model/I18n.model";


export type TabViewOnTabChangeEventType = {
    originalEvent: Event;
    index: number;
    tabUniqueKey: string;
};

type Props = ComponentElementOverflowType & ComponentElementBorderType & {
    id?: string;
    activeIndex?: number;
    style?: any;
    className?: string;
    renderActiveOnly?: boolean;
    children?: any;
    tabPosition?: ComponentElementPosition;
    model: Array<TabModel>;
    tmpValue?: string; // TODO: to remove
    onTabChange?(e: TabViewOnTabChangeEventType): void;
};

type WrappedCompState = Props & {
    // attribute
    activeContainerKey: string;
    tabVisibilities: Array<boolean>;
    contentHeight: number;

    // event
};

type WrappedFormState = {
    onTabChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;

    // TODO: tmp(only for testing, will remove)
    tmpValue: JSMap<string>;
};

/**
 * TabView component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfTabViewPanel extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_6XcUALRLproLnhXu7C9ANMA2zd9M8Ik",
        category: ComponentCategory.PANEL,
        label: I18n.layout.tabViewPanel.label,
        description: I18n.layout.tabViewPanel.description,
        icon: "",
        property: null
    };

    focus(): void {
    }

    private _getPreviousModel(key: string): TabModel {
        const model = this.state.wrappedCompState.model;

        for (let idx = 0; idx < model.length; ++idx) {
            if (key === model[idx].key) {
                return model[idx];
            }
        }

        return {
            label: "",
            key: ""
        };
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, TabViewOnTabChangeEventType>(
                property,
                this);
            const newModelUniqueKeyMap: JSMap<string> = {};

            newState.wrappedCompState.tabVisibilities.length = 0;
            for (let idx = 0; idx < newState.wrappedCompState.model.length; ++idx) {
                newState.wrappedCompState.tabVisibilities.push(true);
                const modelUniqueKey = newState.wrappedCompState.model[idx].key;

                if (newModelUniqueKeyMap.hasOwnProperty(modelUniqueKey)) {
                    // 새로운 모델 아이템의 키가 중복된 상태로 전달된 경우
                    // const removedElement = newState.wrappedCompState.model.splice(idx, 1);
                    // Debug.console.info(modelUniqueKey + " will be ignored because this key is duplicated in new model.");
                    // Debug.console.info(newState.wrappedCompState.model[idx]);
                    newState.wrappedCompState.model[idx] = null;

                    continue;
                }

                if (this._modelUniqueKeyMap.hasOwnProperty(modelUniqueKey)) {
                    /*
                     * 새로운 모델의 아이템 키가 이전 모델에 이미 존재하는 경우
                     * 기존 모델을 그대로 사용하되(새로운 모델 무시) iconClass & label 는 새로운 모델의
                     * 값을 반영한다.
                     */
                    newState.wrappedCompState.model[idx] = {
                        ...this._getPreviousModel(modelUniqueKey),
                        label: newState.wrappedCompState.model[idx].label,
                        iconClass: newState.wrappedCompState.model[idx].iconClass
                    };
                    // Debug.console.info(modelUniqueKey + " will be ignored because this key is already existed in original model.");
                }

                newModelUniqueKeyMap[modelUniqueKey] = "_dummy_";
            }

            for (let idx = newState.wrappedCompState.model.length - 1; idx >= 0; --idx) {
                if (newState.wrappedCompState.model[idx] === null) {
                    newState.wrappedCompState.model.splice(idx, 1);
                    newState.wrappedCompState.tabVisibilities.splice(idx, 1);
                }
            }

            this._modelUniqueKeyMap = newModelUniqueKeyMap;
            this._setCalcVisibilityDone(false);

            // onEvent 계열 함수 호출 후에는 container key를 emit한다.
            if (this.state.displayMode === ComponentElementDisplayMode.INIT) {
                this._currentDisplayMode = null;
            } else {
                if (this._currentDisplayMode !== this.state.displayMode) {
                    this._currentDisplayMode = this.state.displayMode;
                    this._needEmitContainerKey = true;
                }
            }

            if (newState.wrappedCompState.hasOwnProperty("activeIndex") && newState.wrappedCompState.activeIndex >= 0) {
                const model = newState.wrappedCompState.model;
                if (model && _.isArray(model) && newState.wrappedCompState.activeIndex < model.length) {
                    // set activeContainerKey
                    newState.wrappedCompState.activeContainerKey = model[newState.wrappedCompState.activeIndex].key;
                    this.setState(newState, this._callbackSetState);
                } else {
                    // Debug.console.error("The activeIndex must be smaller than the length of model");
                    if (model && _.isArray(model)) {
                        this.setState(newState);
                    }
                }
            } else {
                this.setState(newState, this._callbackNullSetState);
            }
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    emitFormState(value: string): void {
    }

    emitContainerKey(value: string): void {
    }

    emitHasContainerKey(): void {
    }

    onHandlerCallback(): void {
        super.onHandlerCallback();
        // onEvent 계열 함수 호출 후에는 container key를 emit한다.

        if (this.state.displayMode !== ComponentElementDisplayMode.INIT) {
            this._needEmitContainerKey = true;
        }

        this._emitContainerKeyAfterOnEvent();
    }

    noDisplayInheritance() {
    }

    @ReactComponentElementInit(BfTabViewPanel)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    static defaultProps = {
    };

    private _ref: object;
    // visibility 검증을 수행했는지 여부
    private _calcVisibilityDone: boolean;
    // model uniqueKey map
    private _modelUniqueKeyMap: JSMap<string>;
    // state 변화(handleChange) 이외에 containerKey를 emit해야 할 지 여부(onEnvent 계열 함수 호출 시 동작)
    private _needEmitContainerKey: boolean;
    private _currentContainerKey: string;

    private _currentDisplayMode: ComponentElementDisplayMode;

    public _getCalcVisibilityDone(): boolean {
        return this._calcVisibilityDone;
    }

    public _setCalcVisibilityDone(calcVisibilityDone): void {
        this._calcVisibilityDone = calcVisibilityDone;
    }

    constructor(props?: Props) {
        super(props);

        const tabVisibilities: Array<boolean> = new Array<boolean>();

        this._calcVisibilityDone = false;
        this._modelUniqueKeyMap = {};
        this._needEmitContainerKey = false;
        this._currentContainerKey = null;
        this._currentDisplayMode = null;

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                activeIndex: (this.props.activeIndex) ? this.props.activeIndex : 0,
                activeContainerKey: null,
                tabVisibilities: tabVisibilities,
                model: null,
                contentHeight: null
            },
            wrappedFormState: {},
            tmpValue: {}
        };

        this._setRef = this._setRef.bind(this);
        this._handleTabVisibility = this._handleTabVisibility.bind(this);
        this._getCalcVisibilityDone = this._getCalcVisibilityDone.bind(this);
        this._setCalcVisibilityDone = this._setCalcVisibilityDone.bind(this);
        this._getTabModel = this._getTabModel.bind(this);
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }


    /**
     * 전달받는 두 오브젝트가 동일한지 비교
     *
     * @param obj1
     * @param obj2
     * @private
     */
    private _diff(obj1: object, obj2: object): object {
        const _this = this;
        return _.reduce(obj1, function(result, value, key) {
            if (_.isPlainObject(value)) {
                result[key] = _this._diff(value, obj2[key]);
            } else if (!_.isEqual(value, obj2[key])) {
                result[key] = value;
            }

            return result;
        }, {});
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        // if (prevState.show === false && this.state.show === true) {
        //     if (this.state.wrappedCompState.hasOwnProperty("activeIndex") && this.state.wrappedCompState.activeIndex >= 0) {
        //         console.log("set inital formState");
        //         this._callbackSetState();
        //     }
        //     // this._onTabChangeEvent({index: this.state.wrappedCompState.activeIndex, tabUniqueKey: this.state.wrappedCompState.activeContainerKey});
        // }
        this._emitContainerKeyAfterOnEvent();
    }

    componentWillUnmount(): void {
        this._ref = null;
    }

    /*
     * TODO: * TabSet의 state 변화 감지 필요
     */
    private _onTabChangeEvent(e): void {
        // change state and then emit
        const newState = {
            wrappedCompState: {
                ...this.state.wrappedCompState,
                activeIndex: e.index,
                activeContainerKey: e.tabUniqueKey
            }
        };
        /*
        const newState = ObjectHelper.cloneDeep(this.state);
        newState.wrappedCompState.activeIndex = e.index;
        newState.wrappedCompState.activeContainerKey = e.tabUniqueKey;
        */

        // setState is an async function
        this.setState(newState, this._callbackSetState);
    }

    private _callbackNullSetState() {
    }

    private _callbackSetState() {
        this.emitContainerKey(this.state.wrappedCompState.activeContainerKey);

        const currentModel = this.state.wrappedCompState.model[this.state.wrappedCompState.activeIndex];
        const itemValue = currentModel && currentModel.itemValue ? currentModel.itemValue : null;
        const onTabChangeFormState = this.state.wrappedFormState && this.state.wrappedFormState.onTabChange ? this.state.wrappedFormState.onTabChange : null;

        let mergedJson = null;
        const itemValueJson = itemValue && typeof itemValue === "object" ? itemValue : this._convertToJson(itemValue);
        // const onTabChangeFormStateJson = onTabChangeFormState && typeof onTabChangeFormState === "object" ? onTabChangeFormState : this._convertToJson(onTabChangeFormState);

        // if (itemValueJson && onTabChangeFormStateJson) {
        //     mergedJson = _.merge(itemValueJson, onTabChangeFormStateJson);
        // }

        if (itemValueJson && Object.keys(itemValueJson).length > 0) {
            this.emitFormState(JSON.stringify(itemValueJson));
        }
        /*
        if (this.state.wrappedFormState && this.state.wrappedFormState.onTabChange) {
            this.emitFormState(this.state.wrappedFormState.onTabChange);
        }
        */
    }

    private _convertToJson(str): object {
        if (!str) {
            return {};
        }

        try {
            return JSON.parse(str);
        } catch (e) {
            return {};
        }
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            return this._render4ReadOnly();
        }
    }

    private _render(newProps: Props): React.ReactNode {
        const tabContent = this._createTabs();

        if (tabContent) {
            return (
                <TabSet
                    { ...newProps }
                    handleTabVisibility={this._handleTabVisibility}
                    getCalcVisibilityDone={this._getCalcVisibilityDone}
                    setCalcVisibilityDone={this._setCalcVisibilityDone}
                    getTabModel={this._getTabModel}
                    accessibility={this.accessibility}
                    ref={this._setRef}
                >
                    { tabContent }
                </TabSet>
            );
        } else {
            const containerString = {[DomAttribute.CONTAINER.toString()]: ""};
            return (
                <div ref={this._setRef} {...containerString} className="w-100"></div>
            );
        }
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            id: this.elementId
        };

        return this._render(newProps);
    }

    private _setRef(ref): void {
        if (ref) {
            this._ref = ref;
            this.readyComponent();
        }
    }

    private _getTabModel(): Array<TabModel> {
        return this.state.wrappedCompState.model;
    }

    /**
     * TabView의 width & height 변화(resize)에 따라 숨겨질 탭을 계산하여 렌더링
     *
     * @param newTabVisibilities
     * @private
     */
    private _handleTabVisibility(newTabVisibilities: Array<boolean>, callback?: Function, defaultCallback?: Function): void {
        const { tabVisibilities } = this.state.wrappedCompState;
        const tabCount = tabVisibilities.length;
        if (tabCount === newTabVisibilities.length) {
            let changedVisibilities = false;

            for (let idx = 0; idx < tabCount; ++idx) {
                if (tabVisibilities[idx] !== newTabVisibilities[idx]) {
                    changedVisibilities = true;
                    break;
                }
            }

            if (changedVisibilities) {
                const newState = {
                    wrappedCompState: {
                        ...this.state.wrappedCompState,
                        tabVisibilities: ObjectHelper.cloneDeep(newTabVisibilities)
                    }
                };
                this.setState(newState, function() {
                    if (callback && typeof callback === "function") {
                        callback();
                    }
                });
            } else {
                if (defaultCallback && typeof defaultCallback === "function") {
                    defaultCallback();
                }
            }
        } else {
            Debug.console.error("An error occurred during handling tab visibilities");
        }
    }

    /**
     * setProperty로 전달받은 model로 Tab 컴포넌트를 렌더링 한다.
     *
     * @private
     */
    private _createTabs(): any {
        this._currentContainerKey = null;
        const model = this.state.wrappedCompState.model;

        if (model && Array.isArray(model) && model.length > 0) {
            const tabContent = model.map((item, index) => {
                let rcvActiveIndex = this.state.wrappedCompState.activeIndex;
                if (typeof rcvActiveIndex === "string") {
                    rcvActiveIndex = parseInt(rcvActiveIndex, 10);
                }

                if (index === rcvActiveIndex) {
                    this._currentContainerKey = item.key;
                }

                return (
                    <Tab header={item.label} key={item.key} tabUniqueKey={item.key}>
                    </Tab>
                );
            });

            return tabContent;
        } else {
            return null;
        }
    }

    /**
     * onEvent 계열 함수 호출후에는 container key를 emit한다.
     * @private
     */
    private _emitContainerKeyAfterOnEvent(): void {
        if (this.state.displayMode !== ComponentElementDisplayMode.INIT && this._needEmitContainerKey && this._currentContainerKey) {
            this.emitContainerKey(this._currentContainerKey);
            this._needEmitContainerKey = false;
        }
    }
}
